/**
 * WebSocket Connection Handler
 *
 * Manages WebSocket connection, reconnection, and message handling.
 * Implements exponential backoff and Page Visibility API integration.
 * Connection monitoring via native WebSocket events (event-driven architecture).
 */

import { SESSION_STATES } from './state.js';

export class WebSocketHandler {
  constructor(url, messageHandler, stateManager) {
    this.url = url;
    this.messageHandler = messageHandler;
    this.stateManager = stateManager;
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectTimeout = null;
    this.isIntentionalClose = false;
    this.backendConfig = null;  // Store backend config
    this.connectionStartTime = null;  // FIX 4: Track connection timing

    // Rate limit circuit breaker (v1.0.31)
    this.rateLimited = false;
    this.rateLimitUntil = null;
    this.rateLimitCooldownSeconds = 60;  // Default, overridden by backend

    // Bind event handlers
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);

    // Set up event listeners
    this.setupEventListeners();
  }

  /**
   * Update backend configuration
   */
  updateBackendConfig(config) {
    this.backendConfig = config;
    // Update max reconnect attempts if provided
    if (config?.limits?.max_reconnect_attempts) {
      this.maxReconnectAttempts = config.limits.max_reconnect_attempts;
    }
  }

  /**
   * Set up global event listeners
   */
  setupEventListeners() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('pageshow', this.handlePageShow);
  }

  /**
   * Clean up event listeners
   */
  cleanupEventListeners() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  /**
   * Connect to WebSocket server
   */
  connect(sessionId = null) {
    // Prevent duplicate connections (check OPEN and CONNECTING states)
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      console.log(`[WebSocket] Already ${this.ws.readyState === WebSocket.OPEN ? 'connected' : 'connecting'}, ignoring duplicate connect() call`);
      return;
    }

    try {
      // FIX 4: Track connection start time
      this.connectionStartTime = Date.now();

      // Set CONNECTING state
      if (this.stateManager) {
        this.stateManager.setSessionState(SESSION_STATES.CONNECTING);
      }

      // Clear any pending reconnect
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      // Create WebSocket URL with session_id if available
      const wsUrl = sessionId ? `${this.url}?session_id=${sessionId}` : this.url;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected');
        this.reconnectAttempts = 0;

        // Notify state manager
        if (this.stateManager) {
          this.stateManager.setConnectionStatus('connected');
          // Set CONNECTED state (socket open, waiting for backend ready)
          this.stateManager.setSessionState(SESSION_STATES.CONNECTED);
        }

        // Notify UI about connected state
        if (this.messageHandler) {
          this.messageHandler({
            type: 'connection_state',
            state: 'connected'
          });
        }

        // Send stored session_id if reconnecting
        const storedSessionId = this.stateManager?.getSessionId();
        if (storedSessionId && !sessionId) {
          this.send({ type: 'restore_session', session_id: storedSessionId });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Store session_id if provided
          if (data.session_id && this.stateManager) {
            this.stateManager.setSessionId(data.session_id);
          }

          // Handle rate_limit_error from backend (v1.0.31)
          if (data.type === 'rate_limit_error' && data.cooldown_seconds) {
            this.rateLimitCooldownSeconds = data.cooldown_seconds;
            // handleRateLimitClose() will be called by onclose event (1008)
          }

          // Pass message to handler
          if (this.messageHandler) {
            this.messageHandler(data);
          }
        } catch (error) {
          console.error('[WebSocket] Message parse error:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Closed:', event.code, event.reason);

        // Update state
        if (this.stateManager) {
          this.stateManager.setConnectionStatus('disconnected');
          this.stateManager.setSessionState(SESSION_STATES.DISCONNECTED);
        }

        // IDLE TIMEOUT DETECTION (v1.0.41): code 1001 = "Going Away"
        // Don't auto-reconnect after idle timeout (saves resources, user left intentionally)
        if (event.code === 1001 && event.reason.includes('Idle')) {
          console.log('[Idle] Connection closed due to inactivity - session preserved for 30min manual resume');
          return;  // Don't trigger auto-reconnect
        }

        // RATE LIMIT DETECTION (v1.0.31): Stop auto-retry on 1008 (policy violation)
        if (event.code === 1008 && event.reason.includes('Rate limit')) {
          console.log('[WebSocket] Rate limit detected - stopping auto-retry');
          this.handleRateLimitClose();
          return;  // Don't trigger normal reconnect
        }

        // Handle reconnection if not intentional close
        if (!this.isIntentionalClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnect();
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          // Max attempts reached
          if (this.messageHandler) {
            // Use MSG_SERVICE_UNAVAILABLE_FINAL from backend (follows CLAUDE.md "NO HARDCODED TEXT")
            const finalMsg = this.backendConfig?.messages?.service_unavailable_final ||
                            'Service unavailable. Please refresh the page.';
            this.messageHandler({
              type: 'error',
              message: finalMsg
            });
          }
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };

    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      this.reconnect();
    }
  }

  /**
   * Send message to server
   */
  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
        return true;
      } catch (error) {
        console.error('[WebSocket] Send error:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Handle rate limit close - circuit breaker pattern (v1.0.31)
   */
  handleRateLimitClose() {
    this.rateLimited = true;
    this.rateLimitUntil = Date.now() + (this.rateLimitCooldownSeconds * 1000);

    console.log(`[WebSocket] Rate limited until ${new Date(this.rateLimitUntil).toLocaleTimeString()}`);

    // Notify UI to show countdown and retry button
    if (this.messageHandler) {
      this.messageHandler({
        type: 'rate_limit_cooldown',
        cooldownSeconds: this.rateLimitCooldownSeconds,
        message: `Too many connection attempts. Please wait ${this.rateLimitCooldownSeconds} seconds.`
      });
    }
  }

  /**
   * Check if rate limit cooldown has expired
   */
  canRetry() {
    if (!this.rateLimited) {
      return true;
    }

    const now = Date.now();
    if (now >= this.rateLimitUntil) {
      // Cooldown expired - reset rate limit state
      this.rateLimited = false;
      this.rateLimitUntil = null;
      this.reconnectAttempts = 0;  // Reset attempts
      console.log('[WebSocket] Rate limit cooldown expired - retry allowed');
      return true;
    }

    return false;
  }

  /**
   * Manual retry (called by user clicking retry button)
   */
  manualRetry() {
    if (this.canRetry()) {
      console.log('[WebSocket] Manual retry triggered');
      const sessionId = this.stateManager?.getSessionId();
      this.connect(sessionId);
    } else {
      const remainingSeconds = Math.ceil((this.rateLimitUntil - Date.now()) / 1000);
      console.log(`[WebSocket] Manual retry blocked - ${remainingSeconds}s remaining`);
    }
  }

  /**
   * Reconnect with exponential backoff
   */
  reconnect() {
    if (this.isIntentionalClose) {
      return;
    }

    // Circuit breaker: Don't retry if rate limited
    if (this.rateLimited && !this.canRetry()) {
      console.log('[WebSocket] Reconnect blocked by rate limit circuit breaker');
      return;
    }

    this.reconnectAttempts++;

    // Calculate backoff delay (1s, 2s, 4s, 8s, 16s, max 30s)
    const baseDelay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    // Add jitter (0-1000ms) to prevent thundering herd problem
    const jitter = Math.random() * 1000;
    const backoffDelay = baseDelay + jitter;

    console.log(`[WebSocket] Reconnecting in ${backoffDelay / 1000}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    // Notify UI about reconnection attempt
    if (this.messageHandler) {
      this.messageHandler({
        type: 'reconnecting',
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
        nextAttemptIn: backoffDelay
      });
    }

    this.reconnectTimeout = setTimeout(() => {
      const sessionId = this.stateManager?.getSessionId();
      this.connect(sessionId);
    }, backoffDelay);
  }

  /**
   * Handle Page Visibility changes
   */
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Tab became visible
      if (this.ws?.readyState !== WebSocket.OPEN) {
        // Respect circuit breaker - don't reconnect if rate limited
        if (this.rateLimited && !this.canRetry()) {
          const remainingSeconds = Math.ceil((this.rateLimitUntil - Date.now()) / 1000);
          console.log(`[WebSocket] Tab visible but rate limited (${remainingSeconds}s remaining) - not reconnecting`);
          return;
        }

        console.log('[WebSocket] Tab visible, reconnecting');
        this.reconnectAttempts = 0; // Reset attempts on user return
        const sessionId = this.stateManager?.getSessionId();
        this.connect(sessionId);
      }
    }
  }

  /**
   * Handle online event
   */
  handleOnline() {
    console.log('[WebSocket] Network online');
    if (this.ws?.readyState !== WebSocket.OPEN) {
      // Respect circuit breaker - don't reconnect if rate limited
      if (this.rateLimited && !this.canRetry()) {
        console.log('[WebSocket] Network online but rate limited - not reconnecting');
        return;
      }

      this.reconnectAttempts = 0;
      const sessionId = this.stateManager?.getSessionId();
      this.connect(sessionId);
    }
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    console.log('[WebSocket] Network offline');
    if (this.messageHandler) {
      this.messageHandler({
        type: 'offline',
        message: this.backendConfig?.messages?.no_internet || 'No internet connection. Messages will be sent when connection is restored.'
      });
    }
  }

  /**
   * Handle page hide event (mobile background)
   */
  handlePageHide() {
    // Save any pending state
    if (this.stateManager) {
      this.stateManager.saveState();
    }
  }

  /**
   * Handle page show event (mobile foreground)
   */
  handlePageShow(event) {
    if (event.persisted || performance.navigation.type === 2) {
      // Page restored from cache
      if (this.ws?.readyState !== WebSocket.OPEN) {
        // Respect circuit breaker - don't reconnect if rate limited
        if (this.rateLimited && !this.canRetry()) {
          console.log('[WebSocket] Page restored but rate limited - not reconnecting');
          return;
        }

        console.log('[WebSocket] Page restored, reconnecting');
        this.reconnectAttempts = 0;
        const sessionId = this.stateManager?.getSessionId();
        this.connect(sessionId);
      }
    }
  }

  /**
   * Close connection
   */
  close() {
    this.isIntentionalClose = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.cleanupEventListeners();
  }

  /**
   * Get connection state
   */
  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
